import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  FiLogOut,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import logo from "../assets/images/expo-pilot-logo.png";
import { MdHistory, MdOutlineBadge, MdOutlineDashboard } from "react-icons/md";
import { FaRegCircleUser } from "react-icons/fa6";
import { CiCircleList, CiMap } from "react-icons/ci";
import axiosInstance from "../utils/api";
import { GrShieldSecurity } from "react-icons/gr";
import { GiMagicBroom } from "react-icons/gi";
import { getSessionUser } from "../utils/session";

const FEATURE_TO_ACCESS = {
  retroplanning: "retro-plan",
  cleaning: "cleaning",
  hse: "security",
  badging: "badging",
};
const GATED_ACCESSES = Object.values(FEATURE_TO_ACCESS);

const Sidebar = ({ sidebarCollapsed, setSidebarCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [eventName, setEventName] = useState('');

  const { role, accesses = [], permissions = [] } = getSessionUser();
  const isOrga = role === "orga";
  const isSubAdmin = role === "sub_admin";
  const isAdmin = role === "admin";
  const isSuper = role === "super";

  const fetchEventName = async () => {
    try {
      const res = await axiosInstance.get(`/event/${id}`);
      setEventName(res.data.event.name);
    } catch (error) {
      console.log(error);
    }
  };

  const menuItems = [
    { label: "Dashboard", path: `/event/${id}/dashboard`, icon: <MdOutlineDashboard size={20} />, access: "dashboard" },
    { label: "Stand list", path: `/event/${id}/stand-list`, icon: <CiCircleList size={20} />, access: "stands" },
    { label: "Retro plan", path: `/event/${id}/retro-plan`, icon: <CiMap size={20} />, access: "retro-plan" },
    { label: "Cleaning", path: `/event/${id}/cleaning`, icon: <GiMagicBroom size={20} />, access: "cleaning" },
    { label: "Security", path: `/event/${id}/security`, icon: <GrShieldSecurity size={20} />, access: "security" },
    { label: "Badging", path: `/event/${id}/badging`, icon: <MdOutlineBadge size={20} />, access: "badging" },
    { label: "History", path: `/event/${id}/history`, icon: <MdHistory size={20} />, access: "history" },
  ];

  const allowedAdminAccess = permissions
    .map((key) => FEATURE_TO_ACCESS[key])
    .filter(Boolean);

  const visibleMenuItems = (isOrga || isSubAdmin)
    ? menuItems.filter(item => accesses.includes(item.access))
    : (isAdmin || isSuper)
      ? menuItems.filter(item => !GATED_ACCESSES.includes(item.access) || allowedAdminAccess.includes(item.access))
      : menuItems;

  const handleLogoClick = () => {
    if (isAdmin || isSuper) {
      navigate("/dashboard");
      return;
    }
    if (isOrga && !accesses.includes("dashboard")) {
      if (visibleMenuItems.length > 0) {
        navigate(visibleMenuItems[0].path);
        return;
      }
    }
    navigate(`/event/${id}/dashboard`);
  };

  const isActive = (path) => location.pathname.startsWith(path);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  useEffect(() => {
    fetchEventName();
  }, [id]);

  return (
    <aside
      className={`h-screen transition-all duration-300 flex flex-col
        ${sidebarCollapsed ? "w-24" : "w-64"}
        bg-[#051948]
        border-r border-white/10`}
    >
      <div className="relative flex flex-col items-center py-6 gap-3">
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute top-4 right-0 w-8 h-8 rounded-full
      flex items-center justify-center
      border border-white/15 bg-white/5 text-white/60
      hover:text-white hover:border-white/30 transition"
        >
          {sidebarCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
        </button>

        <div
          className={`transition-all duration-300 ${sidebarCollapsed ? "w-12 h-12" : "w-20 h-20"
            } rounded-3xl bg-white shadow-md flex items-center justify-center cursor-pointer`}
          onClick={handleLogoClick}
        >
          <img src={logo} alt="Logo" className="w-3/4 h-3/4 object-contain" />
        </div>

        {!sidebarCollapsed && (
          <div className="text-center px-4">
            <h2 className="text-sm font-semibold text-white truncate">
              {eventName || "Loading..."}
            </h2>
          </div>
        )}
      </div>

      <nav className="flex-1 flex flex-col items-center gap-3 px-3 mt-4">
        {visibleMenuItems.map((item) => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`w-full max-w-xs flex items-center gap-3 px-5 py-3 rounded-2xl
              transition-all duration-200 text-sm font-medium
              ${isActive(item.path)
                ? "bg-white text-[#051948] shadow-md"
                : "text-white/70 hover:bg-white/10 hover:text-white"
              }`}
          >
            {item.icon}
            {!sidebarCollapsed && <span>{item.label}</span>}
          </button>
        ))}
      </nav>

      {role !== "orga" && role !== "sub_admin" && (
        <div className="px-4 pb-3">
          <button
            onClick={() => navigate(`/event/${id}/profile`)}
            className={`w-full flex items-center justify-center gap-3 rounded-2xl
              py-3 text-sm font-medium transition
              ${isActive(`/event/${id}/profile`)
                ? "bg-white text-[#051948] shadow-md"
                : "bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
              }`}
          >
            <FaRegCircleUser />
            {!sidebarCollapsed && <span>Profile</span>}
          </button>
        </div>
      )}

      <div className="px-4 pb-6">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 rounded-2xl
            bg-white/5 py-3 text-white/70
            hover:bg-red-500/20 hover:text-red-300 transition"
        >
          <FiLogOut />
          {!sidebarCollapsed && <span className="text-sm">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;